import { useCallback, useState } from 'react'
import { Upload } from 'lucide-react'
import { cn } from '../lib/utils'
import { validateFile } from '../lib/validation'

interface FileDropzoneProps {
  onFileSelect: (file: File) => void
  accept?: string
  maxSizeMB?: number
  disabled?: boolean
  className?: string
}

export function FileDropzone({
  onFileSelect,
  accept = 'application/pdf',
  maxSizeMB = 10,
  disabled = false,
  className,
}: FileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFile = useCallback(
    (file: File) => {
      setError(null)
      try {
        validateFile(file)
        onFileSelect(file)
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message)
        }
      }
    },
    [onFileSelect]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      if (disabled) return

      const file = e.dataTransfer.files[0]
      if (file) {
        handleFile(file)
      }
    },
    [disabled, handleFile]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!disabled) {
        setIsDragging(true)
      }
    },
    [disabled]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFile(file)
      }
      // Reset input value to allow selecting the same file again
      e.target.value = ''
    },
    [handleFile]
  )

  return (
    <div className={cn('w-full', className)}>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        className={cn(
          'relative flex flex-col items-center justify-center',
          'w-full min-h-[300px] p-8',
          'border-2 border-dashed rounded-xl',
          'transition-all duration-200',
          'cursor-pointer',
          isDragging && !disabled
            ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20'
            : 'border-legal-300 dark:border-legal-600 bg-white dark:bg-legal-800 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-legal-50 dark:hover:bg-legal-700',
          disabled && 'opacity-50 cursor-not-allowed',
          error && 'border-red-500 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
        )}
      >
        <input
          type="file"
          accept={accept}
          onChange={handleFileInput}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          aria-label="Subir archivo"
        />

        <Upload
          className={cn(
            'w-16 h-16 mb-4',
            isDragging && !disabled ? 'text-primary-600 dark:text-primary-400' : 'text-legal-400 dark:text-legal-500',
            error && 'text-red-500 dark:text-red-400'
          )}
        />

        <div className="text-center">
          <p className="text-lg font-medium text-legal-900 dark:text-white mb-2">
            {isDragging
              ? 'Suelta el archivo aquí'
              : 'Arrastra un archivo PDF o haz clic para seleccionar'}
          </p>
          <p className="text-sm text-legal-500 dark:text-legal-400">
            Máximo {maxSizeMB} MB • Solo archivos PDF
          </p>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}
      </div>
    </div>
  )
}
